import type { IObservableArray } from 'mobx';
import { runInAction } from 'mobx';
import { useCallback, useEffect, useState } from 'react';

import type { Campaign, Targetable, Targeting } from '@feathr/blackbox';
import { TargetableClass } from '@feathr/blackbox';
import {
  createTargeting,
  getTargetingByKind,
} from '@feathr/extender/components/Targetings/Targetings.utils';
import { useStore } from '@feathr/extender/state';
import { useDebouncedCallback, useToggle } from '@feathr/hooks';

import {
  SLIDER_DEFAULT_VALUE,
  sliderToValue,
  valueToSlider,
} from './LookalikeAudienceTargeting.utils';

interface IProps {
  targetings: IObservableArray<Targeting>;
  campaign: Campaign;
}

export interface IUseLookalikeAudienceReturn {
  handleSliderChange: (value: number) => void;
  handleToggleChange: (value?: boolean) => void;
  isEnabled: boolean;
  audienceSize: number | undefined;
}

function useLookalikeAudience({ targetings, campaign }: IProps): IUseLookalikeAudienceReturn {
  const currentTargeting = getTargetingByKind({
    campaign,
    kinds: ['omni_lookalike'],
    targetings,
  });

  const { Targetables, Targetings } = useStore();
  const [isEnabled, toggleIsEnabled] = useToggle(currentTargeting !== undefined);

  // Keep track of the current targeting so we can find it when disabling
  const [currentTargetingRef, setCurrentTargetingRef] = useState<Targeting | undefined>(
    currentTargeting,
  );

  const targetableId = currentTargeting?.get('target_data');
  const currentTargetable = targetableId ? Targetables.get(targetableId) : undefined;
  const audienceSize = currentTargetable?.get('audience_size') as number | undefined;

  // Local state for immediate slider feedback
  const [localAudienceSize, setLocalAudienceSize] = useState<number | undefined>(audienceSize);

  // Sync local state with targetable value when it changes externally
  useEffect(() => {
    setLocalAudienceSize(audienceSize);
  }, [audienceSize]);

  function createLookalikeTargetable(value: number = SLIDER_DEFAULT_VALUE): Targetable {
    // Always convert slider position to actual value
    const actualValue = sliderToValue(value);
    return Targetables.create({
      _cls: TargetableClass.omni_lookalike,
      audience_size: actualValue,
      name: 'Lookalike Audience',
    });
  }

  // Update the targetable with the new audience size
  const updateTargetableAudienceSize = useCallback(
    (value: number): void => {
      // Find targeting directly - don't use getTargetingByKind because it requires target_data,
      // and ephemeral targetings might not have it set yet
      const currentTargetingForUpdate = targetings.find(
        (targeting) =>
          targeting.get('kind') === 'omni_lookalike' &&
          targeting.get('parent') === campaign.get('id') &&
          !targeting.get('is_archived'),
      );

      if (!currentTargetingForUpdate) {
        return;
      }

      const currentTargetableId = currentTargetingForUpdate.get('target_data');
      if (!currentTargetableId) {
        // Targeting exists but has no target_data - create a targetable
        // Convert actual value to slider position for createLookalikeTargetable
        const sliderPosition = valueToSlider(value);
        const newTargetable = createLookalikeTargetable(sliderPosition);

        runInAction(() => {
          currentTargetingForUpdate.set({
            target_data: newTargetable.id,
          });
        });
        return;
      }

      // Get the current targetable from the existing targeting
      const currentTargetableForUpdate = Targetables.get(currentTargetableId);

      // If targetable doesn't exist, create a new one
      if (!currentTargetableForUpdate) {
        // Convert actual value to slider position for createLookalikeTargetable
        const sliderPosition = valueToSlider(value);
        const newTargetable = createLookalikeTargetable(sliderPosition);

        runInAction(() => {
          currentTargetingForUpdate.set({
            target_data: newTargetable.id,
          });
        });
        return;
      }

      // Update the same targetable's audience_size with the actual value
      runInAction(() => {
        currentTargetableForUpdate.set({
          audience_size: value,
        });
      });
    },
    [campaign, targetings, Targetables],
  );

  // Debounce the actual update to the targetable (600ms default)
  const debouncedUpdateTargetable = useDebouncedCallback(updateTargetableAudienceSize, 600, [
    campaign,
    targetings,
    Targetables,
  ]);

  function handleSliderChange(value: number): void {
    // Update local state immediately for responsive UI
    setLocalAudienceSize(value);
    // Debounce the actual targetable update
    debouncedUpdateTargetable(value);
  }

  function handleToggleChange(value?: boolean): void {
    toggleIsEnabled();

    // If toggle is enabled, reuse existing targeting or create a new one
    if (value) {
      // Read targetings array to ensure MobX tracks it
      targetings.length;
      // Recompute enabled targeting to get current state - read all properties reactively
      const currentEnabledTargeting = targetings.find((targeting) => {
        const kind = targeting.get('kind');
        const parent = targeting.get('parent');
        const isArchived = targeting.get('is_archived');
        return kind === 'omni_lookalike' && parent === campaign.get('id') && isArchived === false;
      });

      // If there's already an enabled targeting, nothing to do
      if (currentEnabledTargeting) {
        // Update the ref to track this targeting
        setCurrentTargetingRef(currentEnabledTargeting);
        return;
      }

      // Check for archived targeting to reuse instead of creating a new one
      // Read all properties reactively
      const existingArchivedTargeting = targetings.find((targeting) => {
        const kind = targeting.get('kind');
        const parent = targeting.get('parent');
        const isArchived = targeting.get('is_archived');
        return kind === 'omni_lookalike' && parent === campaign.get('id') && isArchived === true;
      });

      if (existingArchivedTargeting) {
        // If the targeting has an ID, it was saved to the backend
        // If it's archived in the backend, we can't unarchive it (backend filters it out)
        // So we should create a new one instead
        if (existingArchivedTargeting.id && !existingArchivedTargeting.isEphemeral) {
          // This targeting was saved to the backend and is archived
          // The backend can't find archived targetings to update them, so create a new one
          const newTargetable = createLookalikeTargetable();
          const newTargeting = createTargeting({
            campaign,
            Targetings,
            targetableId: newTargetable.id,
            kind: 'omni_lookalike',
            targetings,
          });
          setCurrentTargetingRef(newTargeting);
          return;
        }

        // Only unarchive if it's ephemeral (frontend-only) and archived
        // Ephemeral targetings that are archived can be unarchived since they're not in the backend
        if (existingArchivedTargeting.isEphemeral) {
          // Check if the targetable still exists
          const targetableId = existingArchivedTargeting.get('target_data');
          const existingTargetable = targetableId ? Targetables.get(targetableId) : undefined;

          if (!existingTargetable && targetableId) {
            // Targetable was removed, create a new one and update the targeting
            const newTargetable = createLookalikeTargetable();
            runInAction(() => {
              existingArchivedTargeting.set({
                is_archived: false,
                target_data: newTargetable.id,
              });
            });
          } else {
            // Targetable exists, just unarchive the targeting
            runInAction(() => {
              existingArchivedTargeting.set({ is_archived: false });
            });
          }
          // Store reference to the unarchived targeting
          setCurrentTargetingRef(existingArchivedTargeting);
          return;
        }

        // Fallback: create a new one if we can't determine the state
        const newTargetable = createLookalikeTargetable();
        const newTargeting = createTargeting({
          campaign,
          Targetings,
          targetableId: newTargetable.id,
          kind: 'omni_lookalike',
          targetings,
        });
        setCurrentTargetingRef(newTargeting);
        return;
      }

      // No existing targeting found, create a new one
      const newTargetable = createLookalikeTargetable();
      const newTargeting = createTargeting({
        campaign,
        Targetings,
        targetableId: newTargetable.id,
        kind: 'omni_lookalike',
        targetings,
      });
      // Store reference to the newly created targeting
      setCurrentTargetingRef(newTargeting);

      // If toggle is disabled, archive the targeting
    } else if (value === false) {
      // Use the stored reference or find it in the array
      const currentEnabledTargeting =
        currentTargetingRef ||
        targetings.find((targeting) => {
          const kind = targeting.get('kind');
          const parent = targeting.get('parent');
          const isArchived = targeting.get('is_archived');
          return kind === 'omni_lookalike' && parent === campaign.get('id') && isArchived === false;
        });

      if (currentEnabledTargeting) {
        setLocalAudienceSize(0);
        runInAction(() => {
          if (!currentEnabledTargeting.isEphemeral) {
            // Archive non-ephemeral targetings so we can reuse them later
            currentEnabledTargeting.set({ is_archived: true });
          } else {
            // Remove ephemeral targetings - they can't be archived
            targetings.remove(currentEnabledTargeting);
            Targetings.remove(currentEnabledTargeting.id);
          }
          // Clear the reference since we've handled it
          setCurrentTargetingRef(undefined);
        });
      }
    }
  }

  return {
    isEnabled,
    handleSliderChange,
    handleToggleChange,
    // Use local state for immediate slider feedback, fallback to targetable value
    audienceSize: localAudienceSize ?? audienceSize,
  };
}

export default useLookalikeAudience;
